import { useQueryClient } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AdminOrgBalanceRow = {
  id: string;
  name: string;
  slug: string | null;
  stripe_customer_id: string | null;
  /** micro-USD as string to avoid 32-bit overflow */
  credit_balance_micro: string;
};

export type AdminCreditLedgerEntry = {
  id: string;
  type: string;
  amount_micro: string;
  balance_after_micro: string;
  source: string | null;
  reason: string | null;
  granted_by_email: string | null;
  created_at: string;
};

export type AdminOrgBalanceDetail = {
  id: string;
  name: string;
  slug: string | null;
  owner_email: string | null;
  stripe_customer_id: string | null;
  stripe_dashboard_url: string | null;
  credit_balance_micro: string;
  granted_balance_micro: string;
  purchased_balance_micro: string;
  lifetime_paid_micro: string;
  lifetime_free_granted_micro: string;
  recent_transactions: AdminCreditLedgerEntry[];
};

// ─── Documents ────────────────────────────────────────────────────────────────

const BALANCE_SEARCH_QUERY = gql`
  query AdminOrgBalanceSearch($search: String!) {
    adminOrgBalanceSearch(search: $search) {
      id
      name
      slug
      stripe_customer_id
      credit_balance_micro
    }
  }
`;

const BALANCE_DETAIL_QUERY = gql`
  query AdminOrgBalance($org_id: ID!) {
    adminOrgBalance(org_id: $org_id) {
      id
      name
      slug
      owner_email
      stripe_customer_id
      stripe_dashboard_url
      credit_balance_micro
      granted_balance_micro
      purchased_balance_micro
      lifetime_paid_micro
      lifetime_free_granted_micro
      recent_transactions {
        id
        type
        amount_micro
        balance_after_micro
        source
        reason
        granted_by_email
        created_at
      }
    }
  }
`;

const GRANT_CREDIT_MUTATION = gql`
  mutation AdminGrantOrgCredit($org_id: ID!, $amount_cents: Int!, $reason: String!, $idempotency_key: String!) {
    adminGrantOrgCredit(org_id: $org_id, amount_cents: $amount_cents, reason: $reason, idempotency_key: $idempotency_key) {
      id
      name
      slug
      owner_email
      stripe_customer_id
      stripe_dashboard_url
      credit_balance_micro
      granted_balance_micro
      purchased_balance_micro
      lifetime_paid_micro
      lifetime_free_granted_micro
      recent_transactions {
        id
        type
        amount_micro
        balance_after_micro
        source
        reason
        granted_by_email
        created_at
      }
    }
  }
`;

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useAdminOrgBalanceSearch = (search: string, sessionReady: boolean) =>
  useGraphqlQuery<{ adminOrgBalanceSearch: AdminOrgBalanceRow[] }, { search: string }>({
    queryKey: ["adminOrgBalanceSearch", search],
    document: BALANCE_SEARCH_QUERY,
    variables: { search },
    enabled: sessionReady && search.trim().length >= 2,
  });

export const useAdminOrgBalance = (orgId: string | null) =>
  useGraphqlQuery<{ adminOrgBalance: AdminOrgBalanceDetail }, { org_id: string }>({
    queryKey: ["adminOrgBalance", orgId],
    document: BALANCE_DETAIL_QUERY,
    variables: { org_id: orgId ?? "" },
    enabled: !!orgId,
  });

export const useAdminGrantOrgCredit = (onSuccess?: (data: AdminOrgBalanceDetail) => void) => {
  const queryClient = useQueryClient();

  return useGraphqlMutation<
    { adminGrantOrgCredit: AdminOrgBalanceDetail },
    { org_id: string; amount_cents: number; reason: string; idempotency_key: string }
  >({
    document: GRANT_CREDIT_MUTATION,
    onSuccess: (data) => {
      const detail = data.adminGrantOrgCredit;
      queryClient.setQueryData(["adminOrgBalance", detail.id], {
        adminOrgBalance: detail,
      });
      queryClient.invalidateQueries({ queryKey: ["adminOrgBalanceSearch"] });
      queryClient.invalidateQueries({ queryKey: ["adminRecentCreditAdjustments"] });
      onSuccess?.(detail);
    },
  });
};
